import { Permission, Role } from '@/api';
import EnvironmentDevBanner from '@/assets/environment/Environment-Dev.png';
import EnvironmentLocalBanner from '@/assets/environment/Environment-Local.png';
import EnvironmentQaBanner from '@/assets/environment/Environment-QA.png';
import EnvironmentUatBanner from '@/assets/environment/Environment-UAT.png';
import { useAuthContext } from '@/context/auth-context';
import { useHasPermissions } from '@/hooks/use-has-permissions';
import { useUserRole } from '@/hooks/use-user-role';

import { CreatePriceMode } from '@/pages/price-planning/create-price-page/create-price-search-params';
import { usePriceEventQueueStatus } from '@/pages/queries';
import { MAIN_NAV_ITEMS, ROUTES } from '@/routes';
import { connectColors, themeColors } from '@/theme/colors';
import { getEnvironmentLabel, getPreviewLabel } from '@/utils/get-environment';
import {
  Avatar,
  Badge,
  Icon,
  Menu,
  Tooltip,
} from '@lego/connect-components-react';
import {
  Flex,
  HorizontalStack,
  LEGOLogo,
  Navbar,
  Spacer,
  Text,
} from '@lego/klik-ui';
import type { ReactElement } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_MAX_WIDTH } from '../page-layout';
import { DropdownMenu } from './navbar-items/dropdown-menu';
import { MenuItem } from './navbar-items/menu-item';

const ENVIRONMENT_BANNERS: Partial<
  Record<ReturnType<typeof getEnvironmentLabel>, string>
> = {
  Local: EnvironmentLocalBanner,
  Development: EnvironmentDevBanner,
  Staging: EnvironmentQaBanner,
  Uat: EnvironmentUatBanner,
};

export const MAIN_NAV_WIDTH = 1920;

export enum GLOBAL_MODAL {
  Flowhaven = 'Flowhaven',
  RunAutoCalc = 'RunAutoCalc',
  CreatePrice = 'CreatePrice',
  ApprovePrice = 'ApprovePrice',
  UploadPrice = 'UploadPrice',
  UploadCost = 'UploadCost',
  UploadValueBasedPrice = 'UploadValueBasedPrice',
  ImportSimulationFiles = 'ImportSimulationFiles',
  DownloadMismatches = 'DownloadMismatches',
}

interface MainNavbarProps {
  onFlowhavenTriggerClick: (triggerElement: HTMLElement) => void;
  onRunAutoCalcTriggerClick: (triggerElement: HTMLElement) => void;
  onCreatePriceTriggerClick: (triggerElement: HTMLElement) => void;
  onUploadPriceTriggerClick: (triggerElement: HTMLElement) => void;
  onUploadCostTriggerClick: (triggerElement: HTMLElement) => void;
  onUploadValueBasedPriceTriggerClick: (triggerElement: HTMLElement) => void;
  onImportSimulationFilesTriggerClick: (triggerElement: HTMLElement) => void;
  onDownloadMismatchesTriggerClick: (triggerElement: HTMLElement) => void;
  setShowModal: (val: GLOBAL_MODAL) => void;
  sticky?: boolean;
}

export const MainNavbar = ({
  onFlowhavenTriggerClick,
  onRunAutoCalcTriggerClick,
  onCreatePriceTriggerClick,
  onUploadPriceTriggerClick,
  onUploadCostTriggerClick,
  onUploadValueBasedPriceTriggerClick,
  onImportSimulationFilesTriggerClick,
  onDownloadMismatchesTriggerClick,
  setShowModal,
  sticky = true,
}: MainNavbarProps): ReactElement => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { user, logout } = useAuthContext();
  const { isSuccess: isPriceEventQueueStatusSuccess } =
    usePriceEventQueueStatus();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileAnchorRef = useRef<HTMLDivElement>(null);

  const canReadSimulations = useHasPermissions([Permission.ReadSimulations]);
  const canManageLelPrices = useHasPermissions([Permission.ManageLelPrices]);
  const canRunNoveltyCalculation = useHasPermissions([
    Permission.NoveltyCalculation,
  ]);
  const canManageCopyPrice = useHasPermissions([Permission.CopyPrice]);
  const canCompareSimulations = useHasPermissions([
    Permission.CompareSimulations,
  ]);
  const canManageAdminSettings = useHasPermissions([
    Permission.ManageAdminSettings,
  ]);
  const canCreatePrice = useHasPermissions([Permission.CreatePrice]);
  const canReadCalculationSettings = useHasPermissions([
    Permission.ReadCalculationSettings,
  ]);
  const canReadPrices = useHasPermissions([Permission.ReadPrices]);
  const canResetNoveltyPrices = useHasPermissions([
    Permission.DeleteNoveltyPrices,
  ]);
  const canManageValueBasedPrices = useHasPermissions([
    Permission.ManageValueBasedPrices,
  ]);
  const { isAdmin } = useUserRole();
  const currentCreatePriceMode = useMemo(
    () => new URLSearchParams(search).get('mode'),
    [search],
  );
  const userInitials = useMemo(() => {
    const nameParts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
    if (nameParts.length > 1) {
      const first = nameParts[0]?.[0] ?? '';
      const last = nameParts[nameParts.length - 1]?.[0] ?? '';
      return `${first}${last}`.toUpperCase();
    }
    return (nameParts[0]?.slice(0, 2) ?? '').toUpperCase();
  }, [user?.name]);

  const priceSetupOptions = [
    ...(canReadCalculationSettings
      ? [
          {
            label: 'Calculation settings',
            onClick: () => navigate(ROUTES.CalcSettings.staticPath),
            isOpen: pathname.startsWith(ROUTES.CalcSettings.staticPath),
          },
        ]
      : []),
    ...(canRunNoveltyCalculation
      ? [
          {
            label: 'Price Calculator',
            onClick: () => navigate(ROUTES.PriceCalculator.staticPath),
            isOpen: pathname.startsWith(ROUTES.PriceCalculator.staticPath),
          },
        ]
      : []),
  ];

  const pricePlanningOptions = [
    ...(canReadSimulations
      ? [
          {
            label: 'Simulations',
            onClick: () => navigate(ROUTES.SimulationTool.staticPath),
            isOpen: pathname.startsWith(ROUTES.SimulationTool.staticPath),
          },
        ]
      : []),
    ...(canCreatePrice
      ? [
          {
            label: 'Create Prices',
            onClick: (triggerElement: HTMLElement) => {
              onCreatePriceTriggerClick(triggerElement);
              setShowModal(GLOBAL_MODAL.CreatePrice);
            },
            isOpen:
              pathname.startsWith(ROUTES.CreatePrice.staticPath) &&
              currentCreatePriceMode !== CreatePriceMode.Approve,
          },
          {
            label: 'Approve Prices',
            onClick: (triggerElement: HTMLElement) => {
              onCreatePriceTriggerClick(triggerElement);
              setShowModal(GLOBAL_MODAL.ApprovePrice);
            },
            isOpen:
              pathname.startsWith(ROUTES.CreatePrice.staticPath) &&
              currentCreatePriceMode === CreatePriceMode.Approve,
          },
        ]
      : []),
    ...(canManageCopyPrice
      ? [
          {
            label: 'Copy prices',
            onClick: () => navigate(ROUTES.CopyPrice.staticPath),
            isOpen: pathname.startsWith(ROUTES.CopyPrice.staticPath),
          },
        ]
      : []),
  ];

  const pricePlanningSegments = [
    ...(pricePlanningOptions.length > 0
      ? [{ options: pricePlanningOptions }]
      : []),
    ...(canManageLelPrices
      ? [
          {
            title: 'LEGO Extended Line',
            options: [
              {
                label: 'Upload prices',
                tag: 'LEL',
                onClick: (triggerElement: HTMLElement) => {
                  onUploadPriceTriggerClick(triggerElement);
                  setShowModal(GLOBAL_MODAL.UploadPrice);
                },
                isOpen: false,
              },
              {
                label: 'Flowhaven update',
                tag: 'LEL',
                onClick: (triggerElement: HTMLElement) => {
                  onFlowhavenTriggerClick(triggerElement);
                  setShowModal(GLOBAL_MODAL.Flowhaven);
                },
                isOpen: false,
              },
              {
                label: 'Run AutoCalc',
                tag: 'LEL',
                onClick: (triggerElement: HTMLElement) => {
                  onRunAutoCalcTriggerClick(triggerElement);
                  setShowModal(GLOBAL_MODAL.RunAutoCalc);
                },
                isOpen: false,
              },
              {
                label: 'Upload materials costs',
                tag: 'LEL',
                onClick: (triggerElement: HTMLElement) => {
                  onUploadCostTriggerClick(triggerElement);
                  setShowModal(GLOBAL_MODAL.UploadCost);
                },
                isOpen: false,
              },
            ],
          },
        ]
      : []),
  ];

  const adminSegments = [
    ...(canReadPrices
      ? [
          {
            options: [
              {
                label: 'Find price',
                onClick: () => navigate(ROUTES.PriceLookup.staticPath),
                isOpen: pathname.startsWith(ROUTES.PriceLookup.staticPath),
              },
            ],
          },
        ]
      : []),
    ...(canManageValueBasedPrices
      ? [
          {
            options: [
              {
                label: 'Value Based Price Upload',
                onClick: (triggerElement: HTMLElement) => {
                  onUploadValueBasedPriceTriggerClick(triggerElement);
                  setShowModal(GLOBAL_MODAL.UploadValueBasedPrice);
                },
                isOpen: false,
              },
            ],
          },
        ]
      : []),
    ...(canCompareSimulations || canResetNoveltyPrices || isAdmin
      ? [
          {
            title: 'Test & Troubleshoot',
            options: [
              ...(canCompareSimulations
                ? [
                    {
                      label: 'Import Simulation Files',
                      onClick: (triggerElement: HTMLElement) => {
                        onImportSimulationFilesTriggerClick(triggerElement);
                        setShowModal(GLOBAL_MODAL.ImportSimulationFiles);
                      },
                      isOpen: false,
                    },
                    {
                      label: 'Download Mismatches',
                      onClick: (triggerElement: HTMLElement) => {
                        onDownloadMismatchesTriggerClick(triggerElement);
                        setShowModal(GLOBAL_MODAL.DownloadMismatches);
                      },
                      isOpen: false,
                    },
                  ]
                : []),
              ...(canResetNoveltyPrices
                ? [
                    {
                      label: 'Reset Novelty Prices',
                      onClick: () =>
                        navigate(ROUTES.ResetNoveltyPrices.staticPath),
                      isOpen: pathname.startsWith(
                        ROUTES.ResetNoveltyPrices.staticPath,
                      ),
                    },
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      label: 'Developer Dashboard',
                      onClick: () => navigate(ROUTES.DevDashboard.staticPath),
                      isOpen: pathname.startsWith(
                        ROUTES.DevDashboard.staticPath,
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const environmentBannerSrc = ENVIRONMENT_BANNERS[getEnvironmentLabel()];
  const previewLabel = getPreviewLabel();

  return (
    <div style={{ width: '100%', background: connectColors.core.blue[1300] }}>
      <Navbar
        background={connectColors.core.blue[1300]}
        color="white"
        margin="0 auto"
        paddingX="16px"
        position={sticky ? 'sticky' : 'relative'}
        top={sticky ? '0' : undefined}
        zIndex={3}
        flexShrink={0}
        maxWidth={DEFAULT_PAGE_MAX_WIDTH}
      >
        <Navbar.Brand paddingLeft={0} paddingRight="24px">
          <HorizontalStack
            gap="var(--ds-layout-spacing-300, 24px)"
            alignItems="center"
            onClick={() => navigate(ROUTES.Home.path)}
            style={{ cursor: 'pointer' }}
          >
            <LEGOLogo />
            <Text fontSize="lg" fontWeight="700" lineHeight="150%">
              Product Pricing
            </Text>
          </HorizontalStack>
        </Navbar.Brand>
        <Navbar.Segment>
          <MenuItem
            isActive={pathname === ROUTES.Home.staticPath}
            to={ROUTES.Home.path}
          >
            Home
          </MenuItem>
          {priceSetupOptions.length > 0 && (
            <DropdownMenu
              isActive={pathname.startsWith(MAIN_NAV_ITEMS.PriceSetup)}
              iconName="chevron-down-solid"
              title="Price Setup"
              options={priceSetupOptions}
            />
          )}
          {pricePlanningSegments.length > 0 && (
            <DropdownMenu
              isActive={pathname.startsWith(MAIN_NAV_ITEMS.PricePlanning)}
              iconName="chevron-down-solid"
              title="Price Planning"
              segments={pricePlanningSegments}
            />
          )}
          {adminSegments.length > 0 && (
            <DropdownMenu
              isActive={pathname.startsWith(MAIN_NAV_ITEMS.Admin)}
              iconName="chevron-down-solid"
              title="Admin"
              segments={adminSegments}
            />
          )}
        </Navbar.Segment>
        <Spacer />
        <Navbar.Segment height="100%" padding={0}>
          <Flex
            gap="var(--ds-layout-spacing-300, 24px)"
            alignItems="center"
            height="100%"
            padding={0}
          >
            <Tooltip offset={20}>
              <Tooltip.Trigger>
                <Badge
                  size="small"
                  position="top-right"
                  variant={
                    isPriceEventQueueStatusSuccess ? 'positive' : 'negative'
                  }
                  aria-label="System health status"
                >
                  <Icon
                    name="wave-pulse"
                    aria-label="System health"
                    style={{
                      color: themeColors.interactive.primary.subtle.pressed,
                    }}
                  />
                </Badge>
              </Tooltip.Trigger>
              <Tooltip.Content>System health check</Tooltip.Content>
            </Tooltip>
            {previewLabel && (
              <span
                title={previewLabel}
                style={{
                  maxWidth: 320,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '4px 12px',
                  borderRadius: 4,
                  background: connectColors.core.blue[300],
                  color: connectColors.core.blue[1300],
                  fontWeight: 600,
                }}
              >
                {previewLabel}
              </span>
            )}
            {environmentBannerSrc && (
              <img
                src={environmentBannerSrc}
                alt={`${getEnvironmentLabel()} environment`}
                style={{ display: 'block', height: '100%', width: 'auto' }}
              />
            )}
            <Avatar
              ref={profileAnchorRef}
              type="initials"
              name={user?.name ?? ''}
              initials={userInitials}
              size="large"
              border
              role="button"
              tabIndex={0}
              aria-label="User menu"
              onClick={() => setIsProfileMenuOpen((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsProfileMenuOpen((prev) => !prev);
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          </Flex>
          <Menu
            isOpen={isProfileMenuOpen}
            onClose={() => setIsProfileMenuOpen(false)}
            anchorRef={profileAnchorRef}
            aria-labelledby="user-menu"
            position="bottom-right"
            direction="vertical"
            offset={16}
            style={{ width: '200px' }}
          >
            <div
              role="presentation"
              style={{
                padding:
                  'var(--ds-layout-spacing-100) var(--ds-layout-spacing-75)',
              }}
            >
              <span className="body-md-medium">{user?.name}</span>
            </div>
            <Menu.Item
              value="logout"
              onClick={() => {
                logout();
                setIsProfileMenuOpen(false);
              }}
            >
              Logout
            </Menu.Item>
          </Menu>
        </Navbar.Segment>
      </Navbar>
    </div>
  );
};
